/**
 * Audit Logger Utility
 * Tracks user activities and system events for security and compliance
 */

import i18n from '../../i18n';

const al = (key, opts) => i18n.t(`common.audit_sample.${key}`, opts);
const au = (key, opts) => i18n.t(`common.${key}`, opts);

// Audit log storage key
const AUDIT_LOGS_KEY = 'qawaem_audit_logs';
const MAX_LOG_ENTRIES = 10000; // Maximum number of log entries to keep

/**
 * Log levels for audit events
 */
export const AuditLevel = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
};

/**
 * Action types for audit events
 */
export const AuditAction = {
  // Authentication
  LOGIN: 'login',
  LOGOUT: 'logout',
  LOGIN_FAILED: 'login_failed',
  SESSION_EXPIRED: 'session_expired',
  
  // Document Management
  DOCUMENT_CREATE: 'document_create',
  DOCUMENT_UPDATE: 'document_update',
  DOCUMENT_DELETE: 'document_delete',
  DOCUMENT_DOWNLOAD: 'document_download',
  DOCUMENT_PRINT: 'document_print',
  DOCUMENT_CONVERT: 'document_convert',
  
  // User Management
  USER_CREATE: 'user_create',
  USER_UPDATE: 'user_update',
  USER_DELETE: 'user_delete',
  USER_ROLE_CHANGE: 'user_role_change',
  USER_PERMISSION_CHANGE: 'user_permission_change',
  
  // Settings
  SETTINGS_UPDATE: 'settings_update',
  SYSTEM_CONFIG_CHANGE: 'system_config_change',
  BACKUP_CREATE: 'backup_create',
  BACKUP_RESTORE: 'backup_restore',
  
  // Data Management
  DATA_EXPORT: 'data_export',
  DATA_IMPORT: 'data_import',
  DATA_DELETE: 'data_delete',
  DATA_SYNC: 'data_sync',
  
  // System Events
  SYSTEM_ERROR: 'system_error',
  SYSTEM_WARNING: 'system_warning',
  SYSTEM_MAINTENANCE: 'system_maintenance'
};

/**
 * Get current user information
 */
const getCurrentUser = () => {
  try {
    const adminUser = localStorage.getItem('qawaem_admin_user');
    if (adminUser) {
      const user = JSON.parse(adminUser);
      return {
        id: user.id || 'admin',
        name: user.username || 'Admin',
        email: user.email || 'admin@qawaem.sa'
      };
    }
    return {
      id: 'system',
      name: au('audit_system_user'),
      email: 'system@qawaem.sa'
    };
  } catch (error) {
    return {
      id: 'unknown',
      name: au('audit_unknown_user'),
      email: 'unknown@qawaem.sa'
    };
  }
};

/**
 * Get client IP address (simulation - in real app would come from server)
 */
const getClientIP = () => {
  // In a real application, this would be obtained from the server
  // For demo purposes, we'll use a simulated IP
  return localStorage.getItem('client_ip') || '192.168.1.100';
};

/**
 * Create an audit log entry
 */
export const logAuditEvent = (action, resource = '', details = '', level = AuditLevel.INFO, additionalData = {}) => {
  try {
    const user = getCurrentUser();
    const timestamp = new Date().toISOString();
    
    const auditEntry = {
      id: generateLogId(),
      timestamp,
      user: {
        id: user.id,
        name: user.name,
        email: user.email
      },
      action,
      resource,
      details,
      level,
      ipAddress: getClientIP(),
      userAgent: navigator.userAgent,
      sessionId: getSessionId(),
      ...additionalData
    };

    // Get existing logs
    const existingLogs = getAuditLogs();
    
    // Add new log entry at the beginning
    const updatedLogs = [auditEntry, ...existingLogs];
    
    // Keep only the most recent entries
    const trimmedLogs = updatedLogs.slice(0, MAX_LOG_ENTRIES);
    
    // Save to localStorage
    localStorage.setItem(AUDIT_LOGS_KEY, JSON.stringify(trimmedLogs));
    
    // Console logging for development
    console.log(`🔍 [AUDIT] ${level.toUpperCase()}: ${action} - ${resource} - ${details}`, auditEntry);
    
    return auditEntry;
  } catch (error) {
    console.error('Failed to log audit event:', error);
    return null;
  }
};

/**
 * Get all audit logs
 */
export const getAuditLogs = () => {
  try {
    const logs = localStorage.getItem(AUDIT_LOGS_KEY);
    return logs ? JSON.parse(logs) : [];
  } catch (error) {
    console.error('Failed to retrieve audit logs:', error);
    return [];
  }
};

/**
 * Filter audit logs based on criteria
 */
export const filterAuditLogs = (filters = {}) => {
  const logs = getAuditLogs();
  
  return logs.filter(log => {
    // Filter by action type
    if (filters.actionType && (!log.action || typeof log.action !== 'string' || !log.action.includes(filters.actionType))) {
      return false;
    }
    
    // Filter by user
    if (filters.user && log.user && log.user.id !== filters.user) {
      return false;
    }
    
    // Filter by date range
    if (filters.startDate) {
      const logDate = new Date(log.timestamp);
      const startDate = new Date(filters.startDate);
      if (logDate < startDate) {
        return false;
      }
    }
    
    if (filters.endDate) {
      const logDate = new Date(log.timestamp);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      if (logDate > endDate) {
        return false;
      }
    }
    
    // Filter by level
    if (filters.level && log.level !== filters.level) {
      return false;
    }
    
    return true;
  });
};

/**
 * Clear all audit logs
 */
export const clearAuditLogs = () => {
  try {
    const user = getCurrentUser();
    
    // Log the clear action before clearing
    logAuditEvent(
      'audit_logs_cleared',
      'Audit System',
      'All audit logs have been cleared by administrator',
      AuditLevel.WARNING,
      { clearedBy: user.name }
    );
    
    // Wait a moment then clear
    setTimeout(() => {
      localStorage.removeItem(AUDIT_LOGS_KEY);
      console.log('🗑️ All audit logs cleared');
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Failed to clear audit logs:', error);
    return false;
  }
};

/**
 * Export audit logs to JSON
 */
export const exportAuditLogs = (filters = {}) => {
  try {
    const logs = filters && Object.keys(filters).length > 0 ? filterAuditLogs(filters) : getAuditLogs();
    
    const exportData = {
      exportedAt: new Date().toISOString(),
      exportedBy: getCurrentUser(),
      totalEntries: logs.length,
      filters: filters,
      logs: logs
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // Log the export action
    logAuditEvent(
      AuditAction.DATA_EXPORT,
      'Audit Logs',
      `Exported ${logs.length} audit log entries`,
      AuditLevel.INFO,
      { exportedEntries: logs.length, filters }
    );
    
    return true;
  } catch (error) {
    console.error('Failed to export audit logs:', error);
    return false;
  }
};

/**
 * Generate unique log ID
 */
const generateLogId = () => {
  return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

/**
 * Get audit statistics
 */
export const getAuditStats = (timeframe = 'today') => {
  const logs = getAuditLogs();
  
  const now = new Date();
  let startDate;
  
  switch (timeframe) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    default:
      startDate = new Date(0); // All time
  }
  
  const filteredLogs = logs.filter(log => new Date(log.timestamp) >= startDate);
  
  return {
    totalActions: filteredLogs.length,
    loginAttempts: filteredLogs.filter(log => log.action === AuditAction.LOGIN || log.action === AuditAction.LOGIN_FAILED).length,
    documentActions: filteredLogs.filter(log => typeof log.action === 'string' && log.action.includes('document')).length,
    userActions: filteredLogs.filter(log => typeof log.action === 'string' && log.action.includes('user')).length,
    systemActions: filteredLogs.filter(log => typeof log.action === 'string' && log.action.includes('system')).length,
    errors: filteredLogs.filter(log => log.level === AuditLevel.ERROR || log.level === AuditLevel.CRITICAL).length,
    warnings: filteredLogs.filter(log => log.level === AuditLevel.WARNING).length,
    successActions: filteredLogs.filter(log => log.level === AuditLevel.SUCCESS).length
  };
};

// Initialize with some sample audit logs if none exist
export const initializeAuditLogs = () => {
  const existingLogs = getAuditLogs();
  if (existingLogs.length === 0) {
    // Add some sample audit log entries for demonstration
    const sampleLogs = [
      {
        action: AuditAction.LOGIN,
        resource: al('auth_system'),
        details: al('login_success'),
        level: AuditLevel.SUCCESS
      },
      {
        action: AuditAction.DOCUMENT_CREATE,
        resource: al('invoice_resource'),
        details: al('invoice_created'),
        level: AuditLevel.SUCCESS
      },
      {
        action: AuditAction.SETTINGS_UPDATE,
        resource: al('settings_resource'),
        details: al('settings_updated'),
        level: AuditLevel.INFO
      },
      {
        action: AuditAction.BACKUP_CREATE,
        resource: al('backup_resource'),
        details: al('backup_success'),
        level: AuditLevel.SUCCESS
      },
      {
        action: AuditAction.DOCUMENT_DOWNLOAD,
        resource: al('invoice_resource'),
        details: al('download_detail'),
        level: AuditLevel.INFO
      }
    ];
    
    sampleLogs.forEach(log => {
      logAuditEvent(log.action, log.resource, log.details, log.level);
    });
  }
};

export default {
  logAuditEvent,
  getAuditLogs,
  filterAuditLogs,
  clearAuditLogs,
  exportAuditLogs,
  getAuditStats,
  initializeAuditLogs,
  AuditLevel,
  AuditAction
};

